import React, { useCallback, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem, SerializedKit, SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { ThemePanel } from './ThemePanel';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- the dialog frame of the Studio theme dialog, shared on purpose */
import styles from '../../studio/StudioTheme.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseThemeDialogProps {
  isOpen: boolean;
  accountId: number;
  item: CatalogItem;
  kit: SerializedKit | null;
  assets: SerializedKitAsset[];
  onClose: () => void;
  /** The page guards a leave while the draft is not saved. */
  onDirtyChange: (isDirty: boolean) => void;
}

const noop = (): void => undefined;

/**
 * "Edit theme" of the release Overview: the draft Theme panel in the dialog of the Studio theme
 * chip (below 576 px a bottom sheet), so the tiles and the preview get the full width. Save and
 * Cancel of the draft close it. Esc and a click outside close it only when the draft has no change,
 * so a draft is never lost without Cancel. The stale-theme note stays on the Overview summary: the
 * dialog passes no `onShowStale`, so the editor shows none.
 */
const ReleaseThemeDialogComponent: React.FC<ReleaseThemeDialogProps> = ({
  isOpen,
  accountId,
  item,
  kit,
  assets,
  onClose,
  onDirtyChange,
}) => {
  const intl = useIntl();
  const titleId = useId();
  const [isDirty, setIsDirty] = useState(false);
  const changeDirty = useCallback(
    (dirty: boolean) => {
      setIsDirty(dirty);
      onDirtyChange(dirty);
    },
    [onDirtyChange]
  );
  const toggle = useCallback(() => {
    if (!isDirty) {
      onClose();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="xl"
      labelledBy={titleId}
      className={styles.dialog}
      contentClassName={styles.content}
      returnFocusAfterClose
    >
      <div className={styles.sheet} data-testid="release-theme-dialog">
        <span className={styles.grab} aria-hidden="true" />
        <h2 id={titleId} className="visually-hidden">
          {intl.formatMessage(themeMessages.dialogTitle, { title: item.title })}
        </h2>
        <ThemePanel
          accountId={accountId}
          item={item}
          kit={kit}
          assets={assets}
          isCollapsed={false}
          onExpand={noop}
          onCollapse={onClose}
          onDirtyChange={changeDirty}
          isInDialog
        />
      </div>
    </Modal>
  );
};

export const ReleaseThemeDialog = TypedMemo(ReleaseThemeDialogComponent);
